import { grey } from "@ant-design/colors"
import { faCloudDownload, faRotate, faTag } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ConfigProvider, Empty, Input, List, Row, Skeleton, Space, Typography } from "antd"
import theme from 'antd/lib/theme'
import { useRef, useState } from "react"
import { ItemClasses, itemClassName } from "../../common/constants"
import PageSidebar from "../../components/common/PageSidebar"
import SidebarCreateButton from "../../components/common/SidebarCreateButton"
import VSpace from "../../components/common/VSpace"
import ThemableButton from "../../components/Items/ThemableButton"
import { useInvalidateItemCatalog, useItemCatalogQuery } from "./apiQueries"
import groupBy from 'lodash/groupBy'
import { CatalogItem } from "../../domain/catalogItem"
import InfiniteScroll from "react-infinite-scroll-component"
import './ItemCatalogSidebar.css'
import { faInbox } from "@fortawesome/pro-duotone-svg-icons"
import { NavigationService } from "../../services/navigationService"

const { Text } = Typography

const PAGE_SIZE = 50

type CatalogItemRow = {
  key: string
  header: boolean
  name: string
  item?: CatalogItem
}

type GroupedCatalogItems = {
  rows: CatalogItemRow[]
  fetchNextPage: () => void
  hasMore: boolean
  isLoading: boolean
}

const useGroupedCatalogItems = (search: string, retired: boolean): GroupedCatalogItems => {
  const classIds = Object.values(ItemClasses)
  const itemCatalogQuery = useItemCatalogQuery(search, PAGE_SIZE, classIds, [], retired)
  const { pages } = itemCatalogQuery.data ?? { pages: [] }
  const groups = groupBy(pages.flatMap(x => x) ?? [], x => x.class)
  const rows = Object.entries(groups).flatMap(([key, items]) => {
    return [
      {
        key: key.toString(),
        header: true,
        name: itemClassName(items[0].class),
      },
      ...items.map(x => ({
        key: x.guid,
        header: false,
        name: x.name,
        item: x
      }))
    ]
  })


  return {
    rows,
    fetchNextPage: itemCatalogQuery.fetchNextPage,
    hasMore: pages[pages.length - 1]?.length === PAGE_SIZE,
    isLoading: itemCatalogQuery.isLoading
  }
}

export type Props = {
  onSelect: (catalogItem: CatalogItem) => void
  onCreate: () => void
  retired: boolean
  navigationService: NavigationService
}

const ItemCatalogSidebar = ({ onSelect, onCreate, retired, navigationService }: Props) => {
  const [search, setSearch] = useState('')
  const { rows, fetchNextPage, hasMore, isLoading } = useGroupedCatalogItems(search, retired)
  const invalidateItemCatalog = useInvalidateItemCatalog()
  const [selectedCatalogItemGuid, setSelectedCatalogItemGuid] = useState<string | undefined>()

  const headerRef = useRef<HTMLDivElement>(null)

  const handleSelect = (record: CatalogItemRow) => {
    if (!record.header) {
      setSelectedCatalogItemGuid(record.key)
      onSelect(record.item!)
    }
  }

  const headerHeight = headerRef.current?.clientHeight ?? 0

  const ImportItemButton = () => <div
    className={'sidebar-catalog-item'}
    onClick={() => navigationService.setUrl('setup#import-export')}
  >
    <Text type='secondary' style={{ display: 'block', color: '#ffffffd9' }}>
      Import Items
    </Text>
    <Text type="secondary" style={{ display: 'block', color: '#ffffff73' }}>
      <small>
        <Space>
          <FontAwesomeIcon icon={faCloudDownload} />
          Import items from a CSV file
        </Space>
      </small>
    </Text>
  </div>

  return <PageSidebar>
    <VSpace size="large">
      <div ref={headerRef}>
        <VSpace size="large">
          <VSpace style={{ padding: '10px 10px 0 10px' }}>
            <Row justify="space-between" align="middle">
              <Text style={{ color: 'white', fontSize: 18, fontFamily: 'Open Sans, sans-serif' }}>Catalog Items</Text>
              <ThemableButton type="link" colorLink={grey[2]} icon={<FontAwesomeIcon icon={faRotate} />} onClick={invalidateItemCatalog} />
            </Row>
            <Row>
              <Input.Search placeholder="Search" value={search} onChange={x => setSearch(x.target.value ?? '')} allowClear />
            </Row>
          </VSpace>
          <SidebarCreateButton text="Create New Item" onClick={onCreate} />
          <ImportItemButton />
        </VSpace>
      </div>
      <ConfigProvider
        theme={{
          algorithm: theme.darkAlgorithm,
        }}
        renderEmpty={() =>
          <Empty
            description="No items found"
            image={<FontAwesomeIcon icon={faInbox} size="xl" />}
          />
        }
      >
        <div
          id="item-catalog-sidebar-scrollable-div"
          className="react-scrollable-list"
          style={{
            height: `calc(100vh - ${headerHeight}px - 24px)`,
          }}
        >
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchNextPage}
            hasMore={hasMore}
            loader={<Skeleton active />}
            scrollableTarget="item-catalog-sidebar-scrollable-div"
          >
            <List
              dataSource={rows}
              pagination={false}
              loading={isLoading}
              renderItem={record => {
                const categories = record.item?.categories ?? []

                const classNames = ['sidebar-catalog-item']
                if (record.header) {
                  classNames.push('header')
                }
                if (record.key === selectedCatalogItemGuid) {
                  classNames.push('selected')
                }

                const nameTextType = record.header ? 'secondary' : record.item?.retiredAt ? 'danger' : undefined

                return <div
                  className={classNames.join(' ')}
                  onClick={() => handleSelect(record)}
                >
                  <Text type={nameTextType} style={{ display: 'block' }}>
                    {record.name}
                  </Text>
                  {categories && categories.length > 0 &&
                    <Text type="secondary" style={{ display: 'block' }}>
                      <small>{categories.join(', ')}</small>
                    </Text>
                  }
                  {record.item?.tags && record.item.tags.length > 0 &&
                    <Text type="secondary" style={{ display: 'block' }}>
                      <small>
                        <Space>
                          <FontAwesomeIcon icon={faTag} />
                          {record.item?.tags.join(', ')}
                        </Space>
                      </small>
                    </Text>
                  }
                </div>
              }}
            />
          </InfiniteScroll>
        </div>
      </ConfigProvider>
    </VSpace >
  </PageSidebar>
}

export default ItemCatalogSidebar
